import React, { useEffect, useMemo, useState } from 'react';
import CardCommon, { CardCommonType } from '../CardCommon';
import moment from 'moment';
import { Button, DatePicker, InputAdornment } from 'tea-component';
import { CalendarDay, generateCalendarDays, WEEKDAYS } from './utils';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
const { MonthPicker } = DatePicker;

// 1. 使用 I 前缀命名接口
interface ICalendarDay {
  date: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}


// 3. 定义清晰的 Props 接口
interface ICommonCalendarProps extends Omit<CardCommonType, 'children'> {
  defaultValue: string;
  backValue: (value: string[]) => void;
  children?: (value: {
    item: ICalendarDay;
    hoverShowClassName: string;
  }) => JSX.Element;
  loading?: boolean;
}

export default function CommonCalendar({
  defaultValue,
  children,
  backValue,
  loading,
  ...props
}: ICommonCalendarProps) {
  const { isEn } = useLanguage();
  const [value, setValue] = useState(defaultValue);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[][]>([]);

  const monthValue = useMemo(() => {
    return moment(value);
  }, [value]);

  function preMonthClick() {
    setValue(moment(value).subtract(1, 'month').format('YYYY-MM-DD'));
  }

  function nextMonthClick() {
    setValue(moment(value).add(1, 'month').format('YYYY-MM-DD'));
  }

  // useEffect(() => {
  //   // 使用示例
  //   const calendarDays = generateCalendarDays(value);
  //   setCalendarDays(calendarDays);
  //   backValue(calendarDays.flat(1).map((item) => item.date));
  // }, [value]);

  useEffect(() => {
    let isMounted = true;

    const calendarDays = generateCalendarDays(value);

    if (isMounted) {
      setCalendarDays(calendarDays);
      backValue?.(calendarDays.flat(1).map((item) => item.date));
    }

    return () => {
      isMounted = false;
    };
  }, [value]);

  const weekdayHeaders = useMemo(() => {
    const key = isEn ? 'short' : 'cn';

    return WEEKDAYS[key];
  }, [isEn]);

  // 渲染星期标题
  const renderWeekdayHeaders = () => {
    return weekdayHeaders.map((day, index) => (
      <th key={index} className="dbo-weekday-header">
        {day}
      </th>
    ));
  };

  // 渲染日历行
  const renderCalendarRows = () => {
    return calendarDays.map((week, weekIndex) => (
      <tr key={weekIndex}>
        {week.map((day) => {
          const isCurrentMonth =
            moment(day.date).month() === monthValue.month();
          const isToday = moment(day.date).isSame(moment(), 'day');

          return (
            <td
              key={day.date}
              className={`dbo-calendar-day ${
                isCurrentMonth ? 'dbo-current-month' : 'dbo-other-month'
              } ${isToday ? 'dbo-today' : ''}`}
            >
              <div className="dbo-date-container">
                <div className="dbo-day-number">{moment(day.date).date()}</div>
                <div className="dbo-lunar-day">{day.lunar.day}</div>
              </div>
              {/* 这里可以添加其他内容 */}
              {children?.({ item: day, hoverShowClassName: 'dbo-hover-show' })}
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <CardCommon {...props}>
      <InputAdornment
        className="dbo-calendar-input-adornment"
        appearance="pure"
        before={
          <Button
            icon="arrowleft"
            onClick={() => preMonthClick()}
            className="dbo-calendar-pre-month"
          ></Button>
        }
        after={
          <Button
            icon="arrowright"
            onClick={() => nextMonthClick()}
            className="dbo-calendar-next-month"
          ></Button>
        }
      >
        <MonthPicker
          value={monthValue}
          onChange={(value) => setValue(value.format('YYYY-MM-DD'))}
        />
      </InputAdornment>

      {/* 日历表格 */}
      <table
        className={`dbo-calendar-table ${
          loading ? 'dbo-calendar-loading' : ''
        }`}
      >
        <thead>
          <tr>{renderWeekdayHeaders()}</tr>
        </thead>
        <tbody>{renderCalendarRows()}</tbody>
      </table>
    </CardCommon>
  );
}
